import {html, LitElement} from 'lit';
import {customElement, state} from "lit/decorators.js";
import {conf} from "../../config";

import {Store, StoreOpening, Map, WeekDay} from "./models";
import favouriteStoreImg from "/src/assets/images/png/favouriteStore.png";
import {MarkerClusterer} from "@googlemaps/markerclusterer";

import styles from "./store-locator.scss?inline"
import {icons} from "./icons/icons"

import {i18nService} from "../../services/i18nService";
import {markerController} from "./marker";
import {storeService} from "../../services/store-service";
import {storeController} from "./store";
import {position} from "./position";
import {visibilityController} from "./visibility";

@customElement('store-locator')
export class StoreLocator extends LitElement {
  @state() position?: google.maps.LatLng;
  @state() markers?: google.maps.marker.AdvancedMarkerElement[];
  @state() map: google.maps.Map | undefined;
  @state() resultList: Store[] = [];
  @state() stores: Store[] = [];
  @state() currentStore?: Store;
  @state() showInfoBox: boolean = false;
  @state() showLogin: boolean = false;
  @state() isContentExpanded: boolean = false;
  @state() favouriteId: string | undefined;
  @state() searchInput: string = "";

  private readonly countryCode: string;
  private markerCluster?: MarkerClusterer;
  private centerControlCreated = false;

  constructor() {
    super()
    this.countryCode = storeService.getCountryCode()
  }

  async connectedCallback() {
    super.connectedCallback()
    this.stores = await storeService.loadStores()
    window.dispatchEvent(new CustomEvent('store-special'))
    await i18nService.initTranslations()
    await this.loadGoogleMapsScript()
    await this.subscribeForMyStoreId()
  }

  public setWhiteList(storesIds: string[]) {
    this.stores = this.stores.filter(store =>
      storesIds.includes(store.ax_id)
    );
  }

  async disconnectedCallback() {
    super.disconnectedCallback()
  }

  isLoggedIn() {
    return Boolean((window as any)?.NY_CUSTOMER_SERVICE?.currentCustomer);
  }

  // =========================
  // 🔹 GOOGLE MAPS SCRIPT & INITIALIZATION
  // =========================

  async loadGoogleMapsScript(): Promise<void> {
    if (!document.getElementById('google.maps.api')) {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `//maps.googleapis.com/maps/api/js?key=${conf.googleMapsKey}&libraries=geometry,visualization,marker&callback=initMap&loading=async`;
        script.id = 'google.maps.api';
        window.initMap = async () => {
          await this.initMap();
          resolve();
        };
        document.body.appendChild(script);
      });
    } else {
      await this.initMap()
      return Promise.resolve()
    }
  }

  async initMap() {
    this.position = await position.getPos(this.geolocationEnabled.bind(this), this.geolocationDisabled.bind(this))
    this.map = new google.maps.Map(this.shadowRoot?.getElementById('map') as HTMLElement, {
      mapId: "66bd0207585357c3",
      mapTypeControl: false,
      streetViewControl: false,
      center: this.position,
      zoom: 7,
      fullscreenControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      scaleControl: true,
    })

    this.initMarkers()
    this.initMarkerCluster()
    this.initMarkerVisibilitySync()
  }

  // =========================
  // 🔹 MARKERS & CLUSTER MANAGEMENT
  // =========================

  initMarkers() {
    this.markers = markerController.createMarkers(
      this.map,
      this.stores,
      this.selectStore.bind(this)
    );
  }

  initMarkerCluster() {
    this.markerCluster = markerController.createMarkerCluster(this.markers, this.map)
  }

  initMarkerVisibilitySync() {
    visibilityController.syncMarkersOnBoundsChange(
      this.markerCluster,
      this.map,
      this.stores,
    );
  }

  // =========================
  // 🔹 FAVOURITE STORE & STORE SELECTION
  // =========================

  async subscribeForMyStoreId() {
    storeController.subscribeToFavouriteStore(
      this.stores,
      this.map,
      this.favouriteId,
      (id: string) => {
        this.favouriteId = id
      },
      (store: Store | undefined) => {
        this.currentStore = store
      }
    );
  }

  setFavouriteStore(store?: Store) {
    storeController.setFavouriteStore(
      store,
      () => this.isLoggedIn(),
      this.favouriteId,
      this.stores,
      (id: string) => {
        this.favouriteId = id
      },
      () => {
        this.showLogin = true
      }
    );
  }

  selectStore(store: Store) {
    storeController.selectStore(
      store,
      this.currentStore,
      this.map,
      (s: Store) => {
        this.currentStore = s
      },
      () => {
        this.showInfoBox = true
      }
    );
  }

  // =========================
  // 🔹 GEOLOCATION HANDLERS
  // =========================

  geolocationEnabled(pos: google.maps.LatLng) {
    const updatedMap = position.geolocationEnabled(pos, this.stores, this.map)
    this.createCenterControl(updatedMap.pos)
    this.updateMap(updatedMap)
    return pos
  }

  geolocationDisabled() {
    const bounds = position.geolocationDisabled(this.stores, this.countryCode)
    return bounds.getCenter()
  }

  // =========================
  // 🔹 MAP UPDATES & CONTROLS
  // =========================

  updateMap(updatedMap: Map) {
    storeController.panToLocation(15, updatedMap.pos, this.map)
    this.markers?.push(updatedMap.positionMarker)
    this.stores = updatedMap.updatedStores
    this.resultList = updatedMap.closestStores
    position.extendZoom(updatedMap)
  }

  createCenterControl(pos: google.maps.LatLng) {
    if (this.centerControlCreated) return;

    const centerControlDiv = document.createElement('div');
    centerControlDiv.classList.add("my-location-container");
    centerControlDiv.innerHTML = `
    <div class="background"> 
        ${icons.renderMyLocation()}
    </div>
  `;
    this.map?.controls[google.maps.ControlPosition.RIGHT_CENTER].push(centerControlDiv);
    centerControlDiv.addEventListener('click', storeController.panToLocation.bind(this, 15, pos, this.map), {passive: true});

    this.centerControlCreated = true;
  }

  // =========================
  // 🔹 SEARCH & INPUT HANDLING
  // =========================

  onInput(e: CustomEvent) {
    const el = e.target as HTMLInputElement;
    const inputValue = el.value;

    if (!inputValue) {
      this.searchInput = "";
      this.resultList = [];
      return;
    }

    this.searchInput = inputValue;
    this.showInfoBox = false;

    this.resultList = storeController.filterStoresBySearchTerm(
      this.stores,
      inputValue,
      this.favouriteId
    );
  }

  clearInputValue() {
    const element = this.shadowRoot?.getElementById('input') as HTMLInputElement
    element.value = ""
    this.resultList = []
    this.searchInput = ""
  }

  // =========================
  // 🔹 RENDER
  // =========================

  render() {
    const contentExpanded = this.isContentExpanded;
    return html`
      <style>${styles}</style>
      <div class="store-locator">

        <div id="map" class="map"></div>
        <login-dialog @close="${() => this.showLogin = false}"
                      class="login-dialog ${this.showLogin ? "not-logged" : ""}"
                      id="dialog"></login-dialog>
        <div class="overlay ${this.showLogin ? "active" : ""}"></div>
        <div class="content ${contentExpanded ? 'expanded' : ""} ${(!contentExpanded && this.resultList.length == 0) ? 'no-results' : ""}">
          <div class="chevron-container ${this.showInfoBox ? 'switch-color' : ""}">
            <div @click="${() => this.isContentExpanded = !this.isContentExpanded}">
              ${contentExpanded ? icons.renderChevronDown() : icons.renderChevronUp()}
            </div>
          </div>
          ${this.renderResultList()}
          ${this.renderInfoBox()}
        </div>
      </div>
    `;
  }

  renderOpeningTimes() {
    const days: StoreOpening[] = []
    for (let weekDay in WeekDay) {
      const openingTime = this.currentStore?.openings.find(it => it.week_day.toLocaleLowerCase() === weekDay.toLocaleLowerCase())
      if (openingTime) {
        days.push(openingTime)
      } else {
        const closedDay = new StoreOpening({
          week_day: weekDay,
          opening: i18nService?.translate('store.closed'),
          closing: ""
        })
        days.push(closedDay)
      }
    }
    return days.map((store: StoreOpening) => {
      return html`
        <div class="openings">
          <p class="day">${i18nService?.translate(store.week_day.toLocaleLowerCase())}</p>
          <div class="times">
            <div class="time">
              ${store.opening}
              ${store.week_day == "SUNDAY" && !store.closing ? "" : "-"}
              ${store.closing}
            </div>
          </div>
        </div>`
    })
  }

  renderResultList() {
    const hasResults = this.searchInput.length
    return html`
      <div class="result-list-container ${!this.showInfoBox ? 'visible' : 'hidden'}">
        <div class="search-bar">
          <div class="icon magnify">
            ${icons.renderSearch()}
          </div>
          <div class="icon close" @click=${hasResults ? this.clearInputValue : null}>
            ${hasResults ? icons.renderClose() : ""}
          </div>
          <input .value="${this.searchInput}" @keyup="${this.onInput}"
                 id="input" type="text"
                 placeholder="${i18nService?.translate("store.search.options.subtitle")}">
        </div>
        <div class="results">
          ${this.resultList.map(store => {
            return html`
              <div class="result" @click="${() => this.selectStore(store)}">
                <div class="left">
                  ${store.id === this.favouriteId
                          ? html`
                            <div class="isFavourite">
                              ${icons.renderPNG(favouriteStoreImg)}
                            </div>`
                          : ""}
                </div>
                <div class="right">
                  <div class="result-top-content">
                    <p>${store.city}, ${store.headline.toLowerCase()}</p>
                  </div>
                  <div class="result-bottom-content">
                    <p>${store.address_line_1}, ${store.address_line_2} </p>
                    <p>${html`<strong>${store.stringDistance}<strong>`}</p>
                  </div>
                </div>
              </div>`
          })}
        </div>
      </div>`
  }

  renderInfoBox() {
    const navigationLink = `//maps.google.com/maps?daddr=${this.currentStore?.address_line_1}+${this.currentStore?.zip}+${this.currentStore?.city}`
    const store = this.currentStore
    const id = this.favouriteId
    const showBox = this.showInfoBox;
    return html`
      <div class="info-box-container ${showBox ? 'visible' : 'hidden'}">
        <div class="close-icon" @click=${() => {
          this.showInfoBox = false;
          this.isContentExpanded = false;
          markerController.resetIcon(store)
        }}>
          ${icons.renderCloseCircle()}
        </div>
        <div class="info-box-top">
          <div class="info-box-title">
            <div class="header-top">
              <div @click=${() => {
                this.showInfoBox = false
                markerController.resetIcon(store)
              }} class="button-back">
                ${icons.renderBackArrow()}
              </div>
              <p>${store?.headline.toLowerCase()}</p>
            </div>
            <div class="header-middle">
              <p>${store?.address_line_1},</p>
              <p>${store?.address_line_2}</p>
            </div>
            <div class="header-bottom">
              ${store?.stringDistance ? html`<p>
                ${store?.stringDistance}</p>` : ""}
              ${store ? storeController.checkStoreOpenings(store) : ""}
            </div>
          </div>
          <div class="info-box-tiles">
            <div>
              <button class="tile">
                <a href="${navigationLink}" target="_blank" class="navigation">
                  ${icons.renderNavigation()}
                </a>
              </button>
              <p>Route</p>
            </div>
            <div>
              <button class="tile">
                <a href="tel:${store?.phone}">
                  ${icons.renderPhone()}
                </a>
              </button>
              <p>Call</p>
            </div>
            <div>
              <button id="favouriteButton"
                      @click=${() => this.setFavouriteStore(store)}
                      class="tile ${store?.id === id ? "favouriteButton" : ""}">
                <div class="heart">
                  ${store?.id === id
                          ? icons.renderPNG(favouriteStoreImg)
                          : icons.renderHeartGrey()}
                </div>
              </button>
              <p>Like</p>
            </div>
          </div>
        </div>

        <div class="info-box-bottom">
          <div id="openings" class="info-box-openings">
            <p class="title">${i18nService?.translate("store.openings")}</p>
            ${this.renderOpeningTimes()}
            <div class="opening-node">
              <p>${store?.openingNote}</p>
            </div>
          </div>
          <div class="info-box-contact">
            <p class="title">${i18nService?.translate("store.phone")}</p>
            <p class="contact-line">${store?.phone}</p>
          </div>
        </div>
      </div>`
  }
}
